import {
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  Radio,
  Row,
  Space,
  Typography,
} from "antd";
import React, { useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";

const { Title } = Typography;

const RequesterAddressFields = ({ getChildData, validationRules, form }) => {
  const { t } = useTranslation();
  const callparent = (e, name) => {
    getChildData(e, name);
  };

  return (
    <>
      <Col xs={24} md={12}>
        <Form.Item
          className="font-medium"
          label={t("bulkRequest:recipient_name_organization")}
          onChange={(e) =>
            callparent(e.target.value, "RecordMethodReleaseName")
          }
          name="RecordMethodReleaseName"
          rules={validationRules.recipientNameOrg}
        >
          <Input maxLength={50} />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item
          className="font-medium"
          name="recipientAddress1"
          label={t("bulkRequest:address_line_1")}
          onChange={(e) => callparent(e.target.value, "recipientAddress1")}
          rules={validationRules.addressFirst}
        >
          <Input maxLength={150} />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item
          className="font-medium"
          name="recipientAddress2"
          onChange={(e) => callparent(e.target.value, "recipientAddress2")}
          label={t("bulkRequest:address_line_2")}
        >
          <Input maxLength={150} />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item
          name="recipientCity"
          label={t("bulkRequest:city")}
          onChange={(e) => callparent(e.target.value, "recipientCity")}
          rules={validationRules.city}
        >
          <Input maxLength={50} />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item
          name="recipientState"
          label={t("bulkRequest:state")}
          onChange={(e) => callparent(e.target.value, "recipientState")}
          rules={validationRules.state}
        >
          <Input maxLength={50} />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item
          name="recipientZipCode"
          label={t("bulkRequest:zip_code")}
          onChange={(e) => callparent(e.target.value, "recipientZipCode")}
          required
          rules={validationRules.zipCode}
        >
          <Input maxLength={8} />
        </Form.Item>
      </Col>
    </>
  );
};

const RecordRelease = ({ getChildData, bulkRequestData, form }) => {
  const { t, i18n } = useTranslation();
  const onChange = (e) => {
    callparent(e.target.value, "RecordMethod");
  };

  useEffect(() => {
    if (bulkRequestData.RecordMethod === "mail") {
      form.setFieldsValue({
        RecordMethodReleaseName: bulkRequestData.RequesterName,
        recipientAddress1: bulkRequestData.RequesterAddLine1,
        recipientAddress2: bulkRequestData.RequesterAddLine2,
        recipientCity: bulkRequestData.RequesterCity,
        recipientState: bulkRequestData.RequesterState,
        recipientZipCode: bulkRequestData.RequesterZipCode,
      });
    }
  }, [bulkRequestData.RecordMethod]);

  const validatePhone = useCallback(
    (_, value) => {
      const phoneRegex = /^[+]?[0-9]{10,15}$/;
      if (!value || phoneRegex.test(value)) {
        return Promise.resolve();
      }
      return Promise.reject(
        new Error(t("common:please_enter_a_valid_phone_number")),
      );
    },
    [t],
  );

  const callparent = (value, name) => {
    getChildData(value, name);
  };

  const validationRules = useMemo(
    () => ({
      addressFirst: [
        {
          required: true,
          max: 150,
          message: t("bulkRequest:please_enter_address"),
        },
      ],
      city: [
        {
          required: true,
          max: 50,
          message: t("bulkRequest:please_enter_city_name"),
        },
      ],
      state: [
        {
          required: true,
          max: 50,
          message: t("bulkRequest:please_enter_state_name"),
        },
      ],
      zipCode: [
        {
          max: 8,
          required: true,
          message: t("bulkRequest:please_enter_zip_code"),
        },
        {
          pattern: /^[0-9]+$/,
          message: t("bulkRequest:zip_code_must_be_numeric"),
        },
        {
          min: 5,
          message: t("bulkRequest:zip_code_must_be_at_least_5_digits_long"),
        },
      ],
      required: [
        { required: true, message: t("bulkRequest:please_choose_any_option") },
      ],
      recipientNameOrg: [
        {
          required: true,
          max: 50,
          message: t("bulkRequest:please_enter_recipient_name_organization"),
        },
      ],
      recipientFax: [
        {
          required: true,
          max: 20,
          message: t("bulkRequest:please_enter_recipient_fax_number"),
        },
      ],
      recipientEmail: [
        {
          required: true,
          max: 100,
          message: t("bulkRequest:please_enter_email"),
        },
        { type: "email", message: t("bulkRequest:please_enter_valid_email") },
      ],
      nameOrganization: [
        {
          required: true,
          max: 50,
          message: t("bulkRequest:please_enter_name_organization"),
        },
      ],
      phone: [
        {
          required: true,
          max: 20,
          message: t("bulkRequest:please_enter_phone_number"),
        },
        { validator: validatePhone },
      ],
      email: [
        {
          required: true,
          max: 100,
          message: t("bulkRequest:please_enter_email"),
        },
        { type: "email", message: t("bulkRequest:please_enter_valid_email") },
      ],
    }),
    [t, validatePhone],
  );

  useEffect(() => {
    if (bulkRequestData.useRequesterInformation)
      form.setFieldsValue({
        bnameorganization: bulkRequestData.RequesterName,
        bphone: bulkRequestData.RequesterPhoneNumber,
        bemail: bulkRequestData.RequesterEmail,
        // bfax: bulkRequestData.RequesterFaxNumber,
      });
    else
      form.setFieldsValue({
        bnameorganization: bulkRequestData.RecordName,
        bphone: bulkRequestData.RecordPhoneNumber,
        bemail: bulkRequestData.RecordEmail,
        bfax: bulkRequestData.RecordFaxNumber,
      });
  }, [bulkRequestData.useRequesterInformation]);

  return (
    <Card
      styles={{ body: { padding: 0 }, header: { marginBottom: 20 } }}
      bordered={false}
      size="0"
      title={
        <Title
          className="font-semibold"
          style={{ marginBottom: "0" }}
          level={4}
        >
          {t("bulkRequest:recipient_information")}
        </Title>
      }
    >
      <Row className="px-6" gutter={[20, 0]}>
        <Col span={24}>
          <Form.Item
            name="releasemethod"
            required
            className="font-medium"
            label={t("bulkRequest:what_method_of_release")}
            rules={validationRules.required}
          >
            <Radio.Group
              onChange={(e) => onChange(e)}
              value={bulkRequestData.RecordMethod}
            >
              <Space direction="vertical" className="ms-4">
                <Radio value="email" className="font-normal">
                  {t("bulkRequest:email")}
                </Radio>
                <Radio value="fax" className="font-normal">
                  {t("bulkRequest:fax")}
                </Radio>
                <Radio value="mail" className="font-normal">
                  {t("bulkRequest:mail")}
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </Col>

        {bulkRequestData.RecordMethod === "fax" ? (
          <>
            <Col xs={24} md={12}>
              <Form.Item
                className="font-medium"
                label={t("bulkRequest:recipient_name_organization")}
                onChange={(e) =>
                  callparent(e.target.value, "RecordMethodReleaseName")
                }
                name="RecordMethodReleaseName"
                rules={validationRules.recipientNameOrg}
              >
                <Input maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                className="font-medium"
                label={t("bulkRequest:recipient_fax_number")}
                name="rfaxnumber"
                onChange={(e) =>
                  callparent(e.target.value, "RecordMethodReleaseFaxNumber")
                }
                rules={validationRules.recipientFax}
              >
                <Input />
              </Form.Item>
            </Col>
          </>
        ) : bulkRequestData.RecordMethod === "mail" ? (
          <>
            <RequesterAddressFields
              getChildData={getChildData}
              validationRules={validationRules}
              form={form}
            />
          </>
        ) : bulkRequestData.RecordMethod === "email" ? (
          <>
            <Col xs={24} md={12}>
              <Form.Item
                className="font-medium"
                label={t("bulkRequest:recipient_name_organization")}
                onChange={(e) =>
                  callparent(e.target.value, "RecordMethodReleaseName")
                }
                name="RecordMethodReleaseName"
                rules={validationRules.recipientNameOrg}
              >
                <Input maxLength={50} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                className="font-medium"
                label={t("bulkRequest:recipient_email")}
                name="RecordMethodReleaseEmail"
                onChange={(e) =>
                  callparent(e.target.value, "RecordMethodReleaseEmail")
                }
                required
                rules={validationRules.recipientEmail}
              >
                <Input maxLength={100} />
              </Form.Item>
            </Col>
          </>
        ) : null}

        <Col span={24}>
          <Form.Item
            label={t(
              "bulkRequest:if_there_is_a_fee_who_is_the_best_person_for_us_to_contact",
            )}
            required
          >
            <Checkbox
              className="font-medium"
              valuePropName="checked"
              onChange={(e) =>
                callparent(e.target.checked, "useRequesterInformation")
              }
              checked={bulkRequestData.useRequesterInformation}
            >
              {t("bulkRequest:use_requester_information")}
            </Checkbox>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[20, 0]} className="px-6">
        <Col xs={24} md={12}>
          <Form.Item
            className="font-medium"
            required
            label={t("bulkRequest:name_organization")}
            onChange={(e) => callparent(e.target.value, "RecordName")}
            name="bnameorganization"
            rules={validationRules.nameOrganization}
          >
            <Input maxLength={50} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            className="font-medium"
            name="bphone"
            label={t("bulkRequest:phone_number")}
            onChange={(e) => callparent(e.target.value, "RecordPhoneNumber")}
            required
            rules={validationRules.phone}
          >
            <Input maxLength={20} />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            className="font-medium"
            label={t("bulkRequest:email")}
            name="bemail"
            onChange={(e) => callparent(e.target.value, "RecordEmail")}
            required
            rules={validationRules.email}
          >
            <Input maxLength={150} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            className="font-medium"
            name="bfax"
            label={t("bulkRequest:fax_number")}
            onChange={(e) => callparent(e.target.value, "RecordFaxNumber")}
          >
            <Input maxLength={20} />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
};

export default RecordRelease;
